import { browserPresetWorkspace } from "../presets/browser-workspace.js";
import { presetCollectionContract } from "../presets/collection-contracts.js";
import {
  downloadPresetDocument,
  installPresetManagerShell,
  localizePresetManagerShell,
} from "./preset-manager-template.js";
import {
  renderPresetChanges,
  renderPresetLibrary,
} from "./preset-manager-content.js";
import { renderPresetHistoryGraph } from "./preset-manager-history.js";
import { createPresetManagerEventHandlers } from "./preset-manager-events.js";
import {
  buildPresetManagerModel,
  isPlainObject,
  presetCommandErrorMessage,
  presetContractForWorkspace,
  presetDiscardItem,
  presetItemKey,
  presetWorkspaceStateMessage,
  readPresetWorkspaceState,
  translatePresetText,
  trimString,
} from "./preset-workspace-ui-model.js";

const TAG_NAME = "fishy-preset-manager";
const LANGUAGE_CHANGE_EVENT = "fishystuff:languagechange";
const HTMLElementBase = globalThis.HTMLElement ?? class {};
let nextManagerInstanceId = 1;

function toastHelper() {
  return globalThis.window?.__fishystuffToast ?? null;
}

function nextDefaultPresetName(presets, translate) {
  const existingNames = new Set(
    (Array.isArray(presets) ? presets : [])
      .map((preset) => trimString(preset?.name))
      .filter(Boolean),
  );
  for (let index = 1; index <= existingNames.size + 1; index += 1) {
    const candidate = trimString(translate(
      "presets.default_name",
      "Preset {$index}",
      { index },
    ));
    if (candidate && !existingNames.has(candidate)) {
      return candidate;
    }
  }
  throw new TypeError("Default preset name translation must include its index.");
}

export { buildPresetManagerModel };

function setText(element, value) {
  if (element) {
    element.textContent = String(value ?? "");
  }
}

function setHidden(element, hidden) {
  if (element) {
    element.hidden = Boolean(hidden);
  }
}

function setButton(element, { label, disabled = false, hidden = false } = {}) {
  if (!element) {
    return;
  }
  if (label != null) {
    const labelElement = element.querySelector?.("[data-role='button-label']");
    if (labelElement) {
      setText(labelElement, label);
    } else {
      setText(element, label);
    }
    if (element.hasAttribute?.("data-fishy-tooltip")) {
      element.setAttribute("data-fishy-tooltip", String(label));
    }
  }
  element.disabled = Boolean(disabled);
  element.hidden = Boolean(hidden);
}

export class FishyPresetManager extends HTMLElementBase {
  static get observedAttributes() {
    return ["data-preset-collection"];
  }

  constructor() {
    super();
    this.instanceId = nextManagerInstanceId;
    nextManagerInstanceId += 1;
    this._workspaceOverride = null;
    this._contractOverride = null;
    this._workspace = null;
    this._unsubscribe = null;
    this._connected = false;
    this._state = { kind: "uninitialized", collectionKey: "" };
    this._pendingAction = "";
    this._commandEpoch = 0;
    this._errorMessage = "";
    this._errorKind = "";
    this.selectedKey = "";
    this.workspaceView = "library";
    this.detailView = "changes";
    this.libraryQuery = "";
    this.preferredHistoryRevisionId = "";
    this.showAlternateHistoryBranches = false;
    this.focusHistoryLeft = false;
    this.saveAsOpen = false;
    this.lastRenderedPresetId = "";
    const eventHandlers = createPresetManagerEventHandlers(this);
    this.handleClick = eventHandlers.click;
    this.handleInput = eventHandlers.input;
    this.handleChange = eventHandlers.change;
    this.handleSubmit = eventHandlers.submit;
    this.handleKeyDown = eventHandlers.keydown;
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
  }

  get collectionKey() {
    return trimString(this.getAttribute?.("data-preset-collection") || this.dataset?.presetCollection);
  }

  get workspace() {
    return this._workspaceOverride;
  }

  set workspace(value) {
    if (this._workspaceOverride === value) {
      return;
    }
    this._workspaceOverride = value ?? null;
    if (this._connected) {
      this.bindWorkspace();
    }
  }

  get collectionContract() {
    return this._contractOverride;
  }

  set collectionContract(value) {
    this._contractOverride = value ?? null;
    if (this._connected) {
      this.renderState();
    }
  }

  connectedCallback() {
    if (this._connected) {
      return;
    }
    this._connected = true;
    this.renderShell();
    this.addEventListener("click", this.handleClick);
    this.addEventListener("input", this.handleInput);
    this.addEventListener("change", this.handleChange);
    this.addEventListener("submit", this.handleSubmit);
    this.addEventListener("keydown", this.handleKeyDown);
    globalThis.window?.addEventListener?.(LANGUAGE_CHANGE_EVENT, this.handleLanguageChange);
    this.bindWorkspace();
  }

  disconnectedCallback() {
    if (!this._connected) {
      return;
    }
    this._connected = false;
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("input", this.handleInput);
    this.removeEventListener("change", this.handleChange);
    this.removeEventListener("submit", this.handleSubmit);
    this.removeEventListener("keydown", this.handleKeyDown);
    globalThis.window?.removeEventListener?.(LANGUAGE_CHANGE_EVENT, this.handleLanguageChange);
    this.releaseSubscription();
    this._commandEpoch += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-preset-collection" && oldValue !== newValue && this._connected) {
      this.selectedKey = "";
      this.preferredHistoryRevisionId = "";
      this.showAlternateHistoryBranches = false;
      this.bindWorkspace();
    }
  }

  releaseSubscription() {
    if (typeof this._unsubscribe === "function") {
      this._unsubscribe();
    } else {
      this._unsubscribe?.unsubscribe?.();
    }
    this._unsubscribe = null;
  }

  resolveWorkspace() {
    if (this._workspaceOverride) {
      return this._workspaceOverride;
    }
    try {
      return browserPresetWorkspace();
    } catch (error) {
      this._errorMessage = presetCommandErrorMessage(error);
      return null;
    }
  }

  resolveContract() {
    try {
      const contract = this._contractOverride
        ?? presetCollectionContract(this.collectionKey);
      return presetContractForWorkspace(
        contract,
        this._workspace,
        this.collectionKey,
      );
    } catch (error) {
      this._errorMessage = presetCommandErrorMessage(error);
      return {
        key: this.collectionKey,
        info: {
          label: this.collectionKey || "Preset",
          itemLabel: "Preset",
          iconId: "layout-fill",
          fileBaseName: this.collectionKey || "presets",
        },
        fixedPresets: [],
      };
    }
  }

  bindWorkspace() {
    this.releaseSubscription();
    this._workspace = this.resolveWorkspace();
    const collectionKey = this.collectionKey;
    if (!this._workspace || !collectionKey) {
      this._state = {
        kind: "unavailable",
        collectionKey,
        reason: this._errorMessage || "Preset workspace is unavailable.",
      };
      this.renderState();
      return;
    }
    this._state = readPresetWorkspaceState(this._workspace, collectionKey);
    if (typeof this._workspace.subscribe === "function") {
      this._unsubscribe = this._workspace.subscribe(collectionKey, (state) => {
        if (!this._connected) {
          return;
        }
        this._state = state ?? readPresetWorkspaceState(this._workspace, collectionKey);
        this.renderState();
      });
    }
    this.renderState();
    if (typeof this._workspace.load === "function") {
      Promise.resolve(this._workspace.load(collectionKey)).then(() => {
        if (this._connected && this._workspace) {
          this._state = readPresetWorkspaceState(this._workspace, collectionKey);
          this.renderState();
        }
      }).catch((error) => {
        if (this._connected) {
          this._errorMessage = presetCommandErrorMessage(error);
          this.renderState();
        }
      });
    }
  }

  translate(key, fallback, vars = {}) {
    return translatePresetText(key, fallback, vars);
  }

  renderShell() {
    installPresetManagerShell(this, this.instanceId);
    localizePresetManagerShell(this, this.translate.bind(this));
  }
  element(role) {
    return this.querySelector?.(`[data-role="${role}"]`) ?? null;
  }

  action(action) {
    return this.querySelector?.(`[data-action="${action}"]`) ?? null;
  }

  model() {
    const model = buildPresetManagerModel(
      this._state,
      this.resolveContract(),
      this.selectedKey,
      {
        preferredRevisionId: this.preferredHistoryRevisionId,
        showAlternateBranches: this.showAlternateHistoryBranches,
      },
    );
    if (model.selectedKey !== this.selectedKey) {
      this.selectedKey = model.selectedKey;
    }
    this.preferredHistoryRevisionId = trimString(
      model.selectedHistoryAlternates?.primaryRevisionId,
    );
    this.showAlternateHistoryBranches = model.selectedHistoryAlternates?.shown === true;
    return model;
  }

  renderState() {
    if (!this._connected && !this.querySelector?.("[data-role='dialog']")) {
      return;
    }
    const contract = this.resolveContract();
    localizePresetManagerShell(this, this.translate.bind(this));
    const model = buildPresetManagerModel(
      this._state,
      contract,
      this.selectedKey,
      {
        preferredRevisionId: this.preferredHistoryRevisionId,
        showAlternateBranches: this.showAlternateHistoryBranches,
      },
    );
    this.selectedKey = model.selectedKey;
    this.preferredHistoryRevisionId = trimString(
      model.selectedHistoryAlternates?.primaryRevisionId,
    );
    this.showAlternateHistoryBranches = model.selectedHistoryAlternates?.shown === true;
    const info = contract?.info ?? {};
    const label = trimString(info.label) || "Preset";
    const itemLabel = trimString(info.itemLabel) || "Preset";
    const lowerItemLabel = itemLabel.toLocaleLowerCase();
    setText(this.element("manager-title"), label);
    setText(
      this.element("selected-heading"),
      this.translate("presets.selected.heading", "Selected {$item}", { item: itemLabel }),
    );
    setText(
      this.element("selected-name-label"),
      this.translate("presets.selected.name_label", "{$item} name", { item: itemLabel }),
    );
    const visibleError = this._errorMessage
      || (!model.ready
        ? presetWorkspaceStateMessage(this._state)
        : model.savedReferencesMessage);
    const alert = this.element("error-alert");
    const loading = !this._errorMessage
      && (this._state?.kind === "loading" || this._state?.kind === "uninitialized");
    if (alert) {
      alert.className = loading
        ? "alert alert-soft"
        : this._errorKind === "conflict" || this._state?.kind === "conflict"
          ? "alert alert-soft alert-warning"
          : "alert alert-soft alert-error";
      alert.setAttribute("role", loading ? "status" : "alert");
    }
    setText(this.element("error-message"), visibleError);
    setHidden(alert, !visibleError || loading);

    const pending = Boolean(this._pendingAction);
    const current = model.current;
    const compactStatus = this.element("compact-current-status");
    const compactStatusDot = this.element("compact-status-dot");
    setText(
      this.element("compact-current-name"),
      model.baseItem?.name || this.translate("presets.current.unnamed", "Unnamed preset"),
    );
    if (pending) {
      setText(compactStatus, this.translate("presets.autosave.saving", "Saving locally…"));
      if (compactStatusDot) {
        compactStatusDot.className = "loading loading-spinner loading-xs shrink-0";
      }
    } else if (this._errorMessage || !model.ready) {
      setText(compactStatus, this.translate("presets.current.status_unavailable", "Status unavailable"));
      if (compactStatusDot) {
        compactStatusDot.className = "status status-sm status-error shrink-0";
      }
    } else if (current?.modified) {
      setText(compactStatus, this.translate("common.status.modified", "Modified"));
      if (compactStatusDot) {
        compactStatusDot.className = "status status-sm status-warning shrink-0";
      }
    } else {
      setText(compactStatus, this.translate("common.status.saved", "Saved"));
      if (compactStatusDot) {
        compactStatusDot.className = "status status-sm status-success shrink-0";
      }
    }
    const compactName = trimString(this.element("compact-current-name")?.textContent);
    const compactStatusText = trimString(compactStatus?.textContent);
    const compactOpenLabel = `${this.translate("presets.manager.open", "Open preset manager")}: ${compactName} (${compactStatusText})`;
    this.action("open")?.setAttribute?.("aria-label", compactOpenLabel);
    this.action("open")?.setAttribute?.("data-fishy-tooltip", compactOpenLabel);

    const ready = model.ready;
    const history = model.state?.history ?? {};
    const redoChoices = Array.isArray(history.redoChoices) ? history.redoChoices : [];
    const undoLabel = this.translate("common.action.undo", "Undo");
    const redoLabel = this.translate("common.action.redo", "Redo");
    setButton(this.action("undo"), {
      label: undoLabel,
      disabled: pending || !ready || history.canUndo !== true,
    });
    const dialogUndo = this.action("undo-dialog");
    setButton(dialogUndo, {
      label: undoLabel,
      disabled: pending || !ready || !model.selectedIsActive || history.canUndo !== true,
    });
    setButton(this.action("redo"), {
      label: redoLabel,
      disabled: pending || !ready || redoChoices.length === 0,
    });
    const dialogRedo = this.action("redo-dialog");
    setButton(dialogRedo, {
      label: redoLabel,
      disabled: pending || !ready || !model.selectedIsActive || redoChoices.length === 0,
    });
    const basedPreset = model.basedOn?.kind === "preset"
      ? model.presets.find((preset) => preset.id === model.basedOn.id) ?? null
      : null;
    const basedLocalPreset = model.basedOn?.kind === "local"
      ? model.localPresets.find((preset) => preset.id === model.basedOn.id) ?? null
      : null;
    const saveTarget = basedPreset ?? basedLocalPreset;
    const discardItem = presetDiscardItem(model);
    const discardDisabled = pending || !ready || !discardItem;
    const saveAuthorityReady = basedLocalPreset
      ? model.localPresetSources?.kind === "ready"
      : model.savedReferencesReady;
    const saveDisabled = discardDisabled || !saveAuthorityReady;
    const saveAccessibleLabel = saveTarget
      ? this.translate("presets.action.update", "Update \“{$name}\”", { name: saveTarget.name })
      : this.translate("presets.action.save_as", "Save as new {$item}…", { item: lowerItemLabel });
    const compactSave = this.action("save-current");
    setButton(compactSave, {
      label: this.translate("common.action.save", "Save"),
      disabled: saveDisabled,
    });
    compactSave?.setAttribute?.("aria-label", saveAccessibleLabel);
    const dialogSave = this.action("save-current-dialog");
    setButton(dialogSave, {
      label: this.translate("common.action.save", "Save"),
      disabled: saveDisabled || !model.selectedIsActive,
    });
    dialogSave?.setAttribute?.("aria-label", saveAccessibleLabel);
    const discardAccessibleLabel = discardItem
      ? this.translate("presets.action.reset_to", "Reset to \“{$name}\”", { name: discardItem.name })
      : this.translate("presets.action.reset", "Reset current work");
    const compactDiscard = this.action("discard-current");
    setButton(compactDiscard, {
      label: this.translate("common.action.discard", "Discard"),
      disabled: discardDisabled,
    });
    compactDiscard?.setAttribute?.("aria-label", discardAccessibleLabel);
    const dialogDiscard = this.action("discard-current-dialog");
    setButton(dialogDiscard, {
      label: this.translate("common.action.discard", "Discard"),
      disabled: discardDisabled || !model.selectedIsActive,
    });
    dialogDiscard?.setAttribute?.("aria-label", discardAccessibleLabel);

    const saveAsForm = this.element("save-as-form");
    setHidden(saveAsForm, !this.saveAsOpen);
    for (const control of saveAsForm?.querySelectorAll?.("input, button") ?? []) {
      control.disabled = pending || !ready || !model.savedReferencesReady;
    }

    setText(
      this.element("preset-count"),
      model.savedReferencesReady || model.localPresetSources?.kind === "ready"
        ? this.translate("presets.grid.count", "{$count} saved", {
            count: model.presets.length + model.localPresets.length,
          })
        : this.translate("presets.state.unavailable", "Preset storage unavailable"),
    );
    setButton(this.action("import"), {
      disabled: pending || !ready || !model.savedReferencesReady,
    });
    const fileInput = this.element("file-input");
    if (fileInput) {
      fileInput.disabled = pending || !ready || !model.savedReferencesReady;
    }
    this.renderDetailTabs();
    this.renderWorkspaceTabs();
    this.renderLibrary(model);
    setHidden(this.element("preset-loading"), !loading);
    setHidden(this.element("preset-list"), loading);
    setHidden(this.element("preset-count"), loading);
    this.renderInspector(model);
    this.renderChanges(model);
    this.renderHistory(model);
  }

  renderDetailTabs() {
    for (const tab of this.querySelectorAll?.("[data-action='detail-tab']") ?? []) {
      const active = tab.dataset?.view === this.detailView;
      tab.classList?.toggle?.("tab-active", active);
      tab.setAttribute?.("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
    }
    setHidden(this.element("changes-panel"), this.detailView !== "changes");
    setHidden(this.element("history-panel"), this.detailView !== "history");
  }

  renderWorkspaceTabs() {
    for (const tab of this.querySelectorAll?.("[data-action='workspace-tab']") ?? []) {
      const active = tab.dataset?.view === this.workspaceView;
      tab.classList?.toggle?.("tab-active", active);
      tab.setAttribute?.("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
    }
    this.element("library-panel")?.classList?.toggle?.(
      "max-lg:hidden",
      this.workspaceView !== "library",
    );
    this.element("details-panel")?.classList?.toggle?.(
      "max-lg:hidden",
      this.workspaceView !== "details",
    );
  }

  renderLibrary(model) {
    renderPresetLibrary({
      activeIconTemplate: this.element("active-preset-icon-template"),
      empty: this.element("preset-empty"),
      list: this.element("preset-list"),
      model,
      pending: Boolean(this._pendingAction),
      query: this.libraryQuery,
      translate: (key, fallback, vars) => this.translate(key, fallback, vars),
    });
  }

  renderInspector(model) {
    const item = model.selectedItem;
    const pending = Boolean(this._pendingAction);
    setHidden(this.element("selected-active"), !model.selectedIsActive);
    setHidden(this.element("current-work-actions"), !model.selectedIsActive);
    setHidden(this.element("current-history-actions"), !model.selectedIsActive);
    const nameInput = this.element("selected-name");
    if (nameInput) {
      const shouldReplaceValue = globalThis.document?.activeElement !== nameInput
        || this.lastRenderedPresetId !== item?.key;
      if (shouldReplaceValue) {
        nameInput.value = item?.name || "";
      }
      nameInput.disabled = pending || !model.ready || !item;
      nameInput.readOnly = item?.kind !== "preset" || item?.ephemeral === true;
      nameInput.setAttribute(
        "aria-readonly",
        item?.kind !== "preset" || item?.ephemeral === true ? "true" : "false",
      );
    }
    this.lastRenderedPresetId = item?.key || "";
    setText(
      this.element("selected-name-hint"),
      item?.kind === "preset" && item?.ephemeral !== true
        ? this.translate("presets.field.selected_title.hint.saved", "Renaming updates only this saved preset reference.")
        : item?.kind === "local"
          ? this.translate(
              "account.browser.detail",
              "Manage data kept only on this device. These actions do not delete synchronized account data.",
            )
          : this.translate("presets.field.selected_title.hint.readonly", "Built-in preset names cannot be changed."),
    );
    const useSelected = this.action("use-selected");
    setButton(useSelected, {
      label: model.selectedIsActive
        ? this.translate("presets.section.active.title", "Active preset")
        : this.translate("presets.action.set_active", "Activate preset"),
      disabled: pending || !model.ready || !item || model.selectedIsActive,
    });
    useSelected?.classList?.toggle?.("btn-primary", !model.selectedIsActive);
    useSelected?.classList?.toggle?.("btn-outline", model.selectedIsActive);
    if (item) {
      useSelected?.setAttribute?.(
        "aria-label",
        model.selectedIsActive
          ? this.translate("presets.status.active_named", "“{$name}” is the active preset", { name: item.name })
          : this.translate("presets.action.set_active_named", "Activate “{$name}”", { name: item.name }),
      );
    }
    setButton(this.action("clone-selected"), {
      label: this.translate("common.action.clone", "Clone"),
      disabled: pending || !model.ready || !model.savedReferencesReady || !item
        || typeof this._workspace?.clonePreset !== "function",
      hidden: item?.kind === "local",
    });
    setButton(this.action("save-local-to-account"), {
      label: this.translate("", "Save to account"),
      disabled: pending || !model.ready || !model.savedReferencesReady
        || item?.kind !== "local"
        || typeof this._workspace?.clonePreset !== "function",
      hidden: item?.kind !== "local",
    });
    setButton(this.action("export-selected"), {
      label: this.translate("common.action.export", "Export"),
      disabled: pending || !model.ready || !["preset", "local"].includes(item?.kind)
        || (item?.kind === "preset" && (
          !model.savedReferencesReady || typeof this._workspace?.exportCollection !== "function"
        ))
        || (item?.kind === "local" && typeof this._workspace?.exportLocalPreset !== "function"),
      hidden: !["preset", "local"].includes(item?.kind),
    });
    setButton(this.action("delete-selected"), {
      label: this.translate("common.action.delete", "Delete"),
      disabled: pending || !model.ready || !model.savedReferencesReady || item?.kind !== "preset",
      hidden: item?.kind !== "preset",
    });
  }

  renderChanges(model) {
    renderPresetChanges({
      cards: this.element("changes-cards"),
      empty: this.element("changes-empty"),
      list: this.element("changes-list"),
      model,
      summaryElement: this.element("changes-summary"),
      table: this.element("changes-table-wrap"),
      translate: (key, fallback, vars) => this.translate(key, fallback, vars),
    });
  }

  renderHistory(model) {
    const list = this.element("history-list");
    if (!list?.replaceChildren) {
      return;
    }
    const options = {
      currentRevisionId: model.current?.revisionId,
      pending: Boolean(this._pendingAction),
      translate: (key, fallback, vars) => this.translate(key, fallback, vars),
    };
    list.replaceChildren(renderPresetHistoryGraph(model.selectedHistoryGraph, options));
    if (this.focusHistoryLeft) {
      const panel = this.element("history-panel");
      if (panel) {
        panel.scrollLeft = 0;
      }
      this.focusHistoryLeft = false;
    }
    const alternates = model.selectedHistoryAlternates ?? { available: false, shown: false };
    const toggle = this.element("history-alternates-toggle");
    const alternatesVisible = model.ready && alternates.available === true;
    setHidden(this.element("history-alternates-control"), !alternatesVisible);
    if (toggle) {
      toggle.checked = alternates.shown === true;
      toggle.disabled = Boolean(this._pendingAction) || !alternatesVisible;
    }
    setHidden(this.element("history-empty"), model.selectedHistoryGraph.rows.length > 0 || !model.ready);
  }

  async runCommand(actionName, command, successMessage = "") {
    if (this._pendingAction || typeof command !== "function") {
      return null;
    }
    const epoch = this._commandEpoch + 1;
    this._commandEpoch = epoch;
    this._pendingAction = actionName;
    this._errorMessage = "";
    this._errorKind = "";
    this.renderState();
    try {
      const result = await command();
      if (!this._connected || this._commandEpoch !== epoch) {
        return result;
      }
      this._pendingAction = "";
      const resultKind = isPlainObject(result) ? trimString(result.kind) : "";
      const retainsReadyCurrent = result?.authoritative?.kind === "ready"
        && (
          resultKind === "conflict"
          || result?.boundary === "saved-references"
          || result?.boundary === "local-preset-sources"
        );
      if (retainsReadyCurrent) {
        this._state = result.authoritative;
        this._errorKind = resultKind;
        this._errorMessage = presetWorkspaceStateMessage(result);
      } else if (resultKind) {
        this._state = result;
      } else {
        this._state = readPresetWorkspaceState(this._workspace, this.collectionKey);
      }
      if (resultKind === "conflict" || retainsReadyCurrent) {
        this._errorKind = resultKind;
        this._errorMessage ||= presetWorkspaceStateMessage(result);
      } else if (this._state?.kind === "ready") {
        if (successMessage) {
          toastHelper()?.success?.(successMessage);
        }
      } else {
        this._errorKind = trimString(this._state?.kind);
        this._errorMessage = presetWorkspaceStateMessage(this._state);
      }
      this.renderState();
      return result;
    } catch (error) {
      if (this._connected && this._commandEpoch === epoch) {
        this._pendingAction = "";
        this._errorKind = "error";
        this._errorMessage = presetCommandErrorMessage(error);
        toastHelper()?.error?.(this._errorMessage);
        this.renderState();
      }
      return null;
    }
  }

  commandOptions(extra = {}) {
    return {
      ...extra,
      expectedRevision: this._state?.revision,
    };
  }

  showSaveAs() {
    if (!this.model().savedReferencesReady) {
      return;
    }
    this.saveAsOpen = true;
    this.renderState();
    const input = this.element("save-as-name");
    if (input) {
      const model = this.model();
      input.value = nextDefaultPresetName(model.presets, this.translate.bind(this));
      queueMicrotask(() => input.focus?.());
    }
  }

  openManager() {
    this.workspaceView = "library";
    this.renderWorkspaceTabs();
    const dialog = this.element("dialog");
    if (dialog && !dialog.open) {
      dialog.showModal?.();
    }
  }

  selectItem(itemKey) {
    this.selectedKey = trimString(itemKey);
    this.workspaceView = "details";
    this.preferredHistoryRevisionId = "";
    this.showAlternateHistoryBranches = false;
    this.focusHistoryLeft = true;
    this.renderState();
    const detailsPanel = this.element("details-panel");
    if (detailsPanel) {
      detailsPanel.scrollTop = 0;
    }
  }

  async saveCurrent() {
    const model = this.model();
    if (!presetDiscardItem(model) || this._pendingAction) {
      return null;
    }
    if (model.basedOn?.kind === "local") {
      return this.updateCurrentLocalPreset();
    }
    if (model.basedOn?.kind === "preset") {
      if (!model.savedReferencesReady) return null;
      return this.updateCurrentPreset();
    }
    if (model.basedOn?.kind === "fixed" && model.baseItem) {
      if (!model.savedReferencesReady) return null;
      const name = nextDefaultPresetName(model.presets, this.translate.bind(this));
      return this.createPresetFromCurrent(name);
    }
    if (!model.savedReferencesReady) return null;
    this.openManager();
    this.showSaveAs();
    return null;
  }

  async discardCurrent() {
    const model = this.model();
    const discardItem = presetDiscardItem(model);
    if (
      !discardItem
      || this._pendingAction
    ) {
      return null;
    }
    const command = model.baseItem
      ? () => this._workspace?.resetCurrent?.(this.collectionKey, this.commandOptions())
      : () => this._workspace?.useFixed?.(
        this.collectionKey,
        discardItem.id,
        this.commandOptions(),
      );
    return this.runCommand(
      "discard",
      command,
      this.translate("presets.toast.discarded", "Current work reset to its base preset."),
    );
  }

  async saveAs() {
    if (!this.model().savedReferencesReady) {
      return null;
    }
    const input = this.element("save-as-name");
    const name = trimString(input?.value);
    if (!name) {
      input?.focus?.();
      return null;
    }
    return this.createPresetFromCurrent(name);
  }

  async createPresetFromCurrent(nameValue) {
    if (!this.model().savedReferencesReady) {
      return null;
    }
    const name = trimString(nameValue);
    if (!name || typeof this._workspace?.saveAs !== "function") {
      return null;
    }
    const result = await this.runCommand(
      "save-as",
      () => this._workspace.saveAs(this.collectionKey, this.commandOptions({ name })),
      this.translate("presets.toast.created", "Saved new preset \“{$name}\”.", { name }),
    );
    if (result?.kind === "ready") {
      const createdPresetId = trimString(result.effect?.presetId);
      if (createdPresetId) {
        this.selectedKey = presetItemKey("preset", createdPresetId);
      }
      this.saveAsOpen = false;
      this.renderState();
    }
    return result;
  }

  async useSelected() {
    const model = this.model();
    const item = model.selectedItem;
    if (!item) {
      return;
    }
    const options = this.commandOptions({
      ...(item.kind === "local" ? { expectedPresetRevision: item.revision } : {}),
    });
    const command = item.kind === "fixed"
      ? this._workspace?.useFixed?.bind(this._workspace, this.collectionKey, item.id, options)
      : item.kind === "local"
        ? this._workspace?.useLocalPreset?.bind(this._workspace, this.collectionKey, item.id, options)
        : this._workspace?.usePreset?.bind(this._workspace, this.collectionKey, item.id, options);
    await this.runCommand(
      "use-selected",
      command,
      this.translate("presets.toast.applied", "Using \“{$name}\”.", { name: item.name }),
    );
  }

  async activateItem(itemKey) {
    const normalized = trimString(itemKey);
    if (!normalized) {
      return null;
    }
    this.selectedKey = normalized;
    this.preferredHistoryRevisionId = "";
    this.showAlternateHistoryBranches = false;
    return this.useSelected();
  }

  async updateCurrentPreset() {
    const model = this.model();
    if (!model.savedReferencesReady) {
      return;
    }
    const preset = model.basedOn?.kind === "preset"
      ? model.presets.find((candidate) => candidate.id === model.basedOn.id)
      : null;
    if (!preset || typeof this._workspace?.updatePreset !== "function") {
      return;
    }
    return this.runCommand(
      "update",
      () => this._workspace.updatePreset(
        this.collectionKey,
        preset.id,
        this.commandOptions({ expectedPresetRevision: preset.revision }),
      ),
      this.translate("presets.toast.saved", "Updated \“{$name}\”.", { name: preset.name }),
    );
  }

  async updateCurrentLocalPreset() {
    const model = this.model();
    const preset = model.basedOn?.kind === "local"
      ? model.localPresets.find((candidate) => candidate.id === model.basedOn.id)
      : null;
    if (
      model.localPresetSources?.kind !== "ready"
      || !preset
      || typeof this._workspace?.updateLocalPreset !== "function"
    ) {
      return null;
    }
    return this.runCommand(
      "update-local",
      () => this._workspace.updateLocalPreset(
        this.collectionKey,
        preset.id,
        this.commandOptions({ expectedPresetRevision: preset.revision }),
      ),
      this.translate("presets.toast.saved", "Updated \“{$name}\”", { name: preset.name }),
    );
  }

  async renameSelected(input) {
    const model = this.model();
    if (!model.savedReferencesReady) {
      return;
    }
    const preset = model.selectedItem?.kind === "preset" ? model.selectedItem : null;
    const name = trimString(input?.value);
    if (!preset || !name || name === preset.name || typeof this._workspace?.renamePreset !== "function") {
      if (input && preset) {
        input.value = preset.name;
      }
      return;
    }
    await this.runCommand(
      "rename",
      () => this._workspace.renamePreset(
        this.collectionKey,
        preset.id,
        this.commandOptions({ name, expectedPresetRevision: preset.revision }),
      ),
      this.translate("presets.toast.renamed", "Renamed to \“{$name}\”.", { name }),
    );
  }

  async cloneSelected() {
    const model = this.model();
    if (!model.savedReferencesReady) {
      return;
    }
    const item = model.selectedItem;
    if (!item || item.kind === "local") {
      return;
    }
    const name = this.translate("presets.copy_name", "{$name} copy", { name: item.name });
    const command = typeof this._workspace?.clonePreset === "function"
      ? () => this._workspace.clonePreset(
          this.collectionKey,
          { kind: item.kind, id: item.id },
          this.commandOptions({
            name,
            ...(item.kind === "preset" ? { expectedPresetRevision: item.revision } : {}),
          }),
        )
      : null;
    const result = await this.runCommand(
      "clone",
      command,
      this.translate("presets.toast.copied", "Created \“{$name}\”.", { name }),
    );
    const createdPresetId = trimString(result?.effect?.presetId);
    if (createdPresetId) {
      this.selectedKey = presetItemKey("preset", createdPresetId);
      this.renderState();
    }
  }

  async saveLocalToAccount() {
    const model = this.model();
    const localPreset = model.selectedItem?.kind === "local" ? model.selectedItem : null;
    if (
      !model.savedReferencesReady
      || !localPreset
      || typeof this._workspace?.clonePreset !== "function"
    ) {
      return;
    }
    const result = await this.runCommand(
      "save-local-to-account",
      () => this._workspace.clonePreset(
        this.collectionKey,
        { kind: "local", id: localPreset.id },
        this.commandOptions({
          name: localPreset.name,
          expectedPresetRevision: localPreset.revision,
        }),
      ),
      this.translate(
        "presets.toast.created",
        "Saved new preset “{$name}”.",
        { name: localPreset.name },
      ),
    );
    const createdPresetId = trimString(result?.effect?.presetId);
    if (createdPresetId) {
      this.selectedKey = presetItemKey("preset", createdPresetId);
      this.renderState();
    }
    return result;
  }

  async deleteSelected() {
    const model = this.model();
    if (!model.savedReferencesReady) {
      return;
    }
    const preset = model.selectedItem?.kind === "preset" ? model.selectedItem : null;
    if (!preset || typeof this._workspace?.deletePreset !== "function") {
      return;
    }
    const confirmed = globalThis.window?.confirm?.(
      this.translate("presets.confirm.delete", "Delete \“{$name}\”? Current work and history will remain available.", {
        name: preset.name,
      }),
    );
    if (confirmed === false) {
      return;
    }
    await this.runCommand(
      "delete",
      () => this._workspace.deletePreset(
        this.collectionKey,
        preset.id,
        this.commandOptions({ expectedPresetRevision: preset.revision }),
      ),
      this.translate("presets.toast.deleted", "Deleted \“{$name}\”.", { name: preset.name }),
    );
  }

  async exportSelected() {
    const model = this.model();
    const preset = ["preset", "local"].includes(model.selectedItem?.kind)
      ? model.selectedItem
      : null;
    if (
      !preset
      || this._pendingAction
      || (preset.kind === "preset" && (
        !model.savedReferencesReady || typeof this._workspace?.exportCollection !== "function"
      ))
      || (preset.kind === "local" && typeof this._workspace?.exportLocalPreset !== "function")
    ) {
      return;
    }
    this._pendingAction = "export";
    this._errorMessage = "";
    this.renderState();
    try {
      const documentValue = preset.kind === "local"
        ? await this._workspace.exportLocalPreset(this.collectionKey, preset.id, {
            expectedPresetRevision: preset.revision,
          })
        : await this._workspace.exportCollection(this.collectionKey, {
            presetIds: [preset.id],
          });
      const contract = this.resolveContract();
      downloadPresetDocument(`${trimString(contract?.info?.fileBaseName) || this.collectionKey || "presets"}.json`, documentValue);
      toastHelper()?.success?.(this.translate("presets.toast.exported", "Preset exported."));
    } catch (error) {
      this._errorMessage = presetCommandErrorMessage(error);
      toastHelper()?.error?.(this._errorMessage);
    } finally {
      this._pendingAction = "";
      this.renderState();
    }
  }

  async importFile(input) {
    if (!this.model().savedReferencesReady) {
      if (input) {
        input.value = "";
      }
      return;
    }
    const file = input?.files?.[0] ?? null;
    if (!file || typeof this._workspace?.importCollection !== "function") {
      return;
    }
    try {
      const interchange = JSON.parse(await file.text());
      const result = await this.runCommand(
        "import",
        () => this._workspace.importCollection(this.collectionKey, interchange, this.commandOptions()),
        this.translate("presets.toast.imported", "Preset imported."),
      );
      const importedPresetId = trimString(result?.effect?.presetIds?.[0]);
      if (importedPresetId) {
        this.selectedKey = presetItemKey("preset", importedPresetId);
        this.renderState();
      }
    } catch (error) {
      this._errorMessage = presetCommandErrorMessage(error);
      toastHelper()?.error?.(this._errorMessage);
      this.renderState();
    } finally {
      input.value = "";
    }
  }

  async useRevision(revisionId) {
    const normalized = trimString(revisionId);
    if (!normalized || typeof this._workspace?.useRevision !== "function") {
      return;
    }
    await this.runCommand(
      "use-revision",
      () => this._workspace.useRevision(this.collectionKey, normalized, this.commandOptions()),
      this.translate("presets.history.version_applied", "Current work now uses the selected version."),
    );
  }

  async undoCurrent() {
    await this.runCommand(
      "undo",
      () => this._workspace?.undo?.(this.collectionKey, this.commandOptions()),
      this.translate("presets.history.undone", "Moved to the previous version."),
    );
  }

  async redoCurrent() {
    await this.runCommand(
      "redo",
      () => this._workspace?.redo?.(this.collectionKey, this.commandOptions()),
      this.translate("presets.history.redone", "Moved to the next version."),
    );
  }

  handleLanguageChange() {
    this.renderState();
  }
}

export function registerPresetManager(registry = globalThis.customElements) {
  if (!registry || typeof registry.define !== "function" || registry.get?.(TAG_NAME)) {
    return;
  }
  registry.define(TAG_NAME, FishyPresetManager);
}

registerPresetManager();
